import { Button } from "./Button.tsx";
import { markColor } from "../lib/group-color.ts";
import { AvailabilityCount, AvailabilityDate, CalendarArrow as Arrow, useSwipe, type DayAvailability } from "./CalendarParts.tsx";
import { ChevronLeftIcon, ChevronRightIcon } from "./Icons.tsx";

/**
 * Seven days, one of them picked.
 *
 * The owner schedule used to be a scrolling agenda of a whole week under day headings, and it read
 * as today's list: the top of the screen was always the nearest day, and nothing above the fold
 * said which day that was or that six more existed below it. This is the fix. The strip is the
 * week, visible in full and never scrolled; the number under each day is how many sessions are on
 * it, so the shape of the week is answered before anything is tapped; and the list underneath is
 * one day, titled with its own date.
 *
 * A dot rather than a count where the day is empty, because "0" is a number the eye stops on and a
 * quiet day is not news. Today is ringed even when another day is selected, so moving through the
 * week never costs you the sense of where you actually are in it.
 *
 * The member home uses the same strip with `dots` in place of the count: a circle around the date
 * per participant, in the participant's mark colour (`markColor`). A solid circle is booked, a
 * dashed circle is a usual day with nothing booked. Two participants on one day share the circle,
 * an arc each. A red dash under the date is a session the business cancelled. The shape carries
 * the meaning, never the colour alone, and the day's label says it in words. A click on a date
 * shows that day in the list under it (the blue tile); with no day picked, today's weekday and date
 * are in the accent colour. Without `onSelect` the days are labels, not controls, and today is the
 * blue tile. `canPrevious` and `canNext` stop the arrows at the ends, a swipe does what the
 * arrows do, and `onBack` shows "Back to today" when the strip is not on today's page.
 *
 * The Book tab uses it with `availability` in place of the count's dot: a filled disc for a day
 * with a free spot, a dashed ring for a day whose sessions are full or started, the bare date for a
 * day with nothing on it (`AvailabilityDate`). The month view draws its days the same way.
 */

/** One participant's mark under a day. */
export type WeekDayDot = {
  /** The participant, one dot each. */
  participantId: string;
  /** The participant's group colour. */
  color: string;
  kind: "booked" | "open" | "cancelled";
};

export type WeekDay = {
  /** `yyyy-MM-dd` in the business's timezone, which is what the sessions are grouped by. */
  key: string;
  /** "Mon" */
  weekday: string;
  /** "8" */
  dayOfMonth: string;
  /** Sessions on the day. The owner schedule shows it; the member home shows `dots` instead. */
  count?: number;
  dots?: WeekDayDot[];
  /** The Book tab: what a member can do on the day. `count` is then the sessions it marks. */
  availability?: DayAvailability;
  /** What a screen reader says for the day, when the default ("Mon 8, 2 sessions") says too little. */
  label?: string;
  isToday: boolean;
};

export function WeekStrip({
  label,
  range,
  days,
  selectedKey,
  onSelect,
  onPrevious,
  onNext,
  canPrevious = true,
  canNext = true,
  onBack,
}: {
  /** "This week", "Next week", or the dates on the member home ("2 - 8 Oct"). */
  label: string;
  /** "8 - 14 Sep", or "Today and the next 6 days" on the member home. */
  range: string;
  days: WeekDay[];
  selectedKey?: string;
  /** Without it the days are labels, and today is the selected tile. */
  onSelect?: (key: string) => void;
  onPrevious: () => void;
  onNext: () => void;
  canPrevious?: boolean;
  canNext?: boolean;
  /** Shows "Back to today" under the days. */
  onBack?: () => void;
}) {
  const { onTouchStart, onTouchEnd } = useSwipe({ onPrevious, onNext, canPrevious, canNext });

  return (
    <div
      className="rounded-card border border-line bg-paper px-1.5 pt-1.5 pb-2.5 shadow-card"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="flex items-center gap-1">
        <Arrow label="Previous week" onClick={onPrevious} disabled={!canPrevious}>
          <ChevronLeftIcon size={20} />
        </Arrow>
        <div className="min-w-0 flex-1 text-center">
          <p className="truncate text-[15px] font-semibold text-ink">{label}</p>
          <p className="text-[12px] text-ink-2 tabular-nums">{range}</p>
        </div>
        <Arrow label="Next week" onClick={onNext} disabled={!canNext}>
          <ChevronRightIcon size={20} />
        </Arrow>
      </div>

      {/*
        A list rather than a row of bare buttons: seven days is a set, and a screen reader user
        should hear how far through it they are rather than seven unrelated controls.
      */}
      <ul className="mt-1.5 flex items-stretch gap-0.5 px-0.5">
        {days.map((day) => {
          const selected = onSelect ? day.key === selectedKey : day.isToday;
          // The circles take the date's ring, so today not picked says itself in the accent colour.
          const todayInk = Boolean(day.dots) && day.isToday && !selected;
          const face = (
            <>
              <span
                className={[
                  "text-[10px] font-semibold tracking-[0.06em] uppercase",
                  selected ? "text-inverse/75" : todayInk ? "text-accent" : "text-ink-3",
                ].join(" ")}
              >
                {day.weekday}
              </span>
              {day.availability ? (
                <AvailabilityDate availability={day.availability} onBlue={selected} isToday={day.isToday}>
                  {day.dayOfMonth}
                </AvailabilityDate>
              ) : day.dots ? (
                <Ringed dots={day.dots} onBlue={selected}>
                  <span className={todayInk ? "text-accent" : undefined}>{day.dayOfMonth}</span>
                </Ringed>
              ) : (
                <span
                  className={[
                    "flex size-7 items-center justify-center rounded-full",
                    "text-[16px] leading-none font-semibold tabular-nums",
                    day.isToday && !selected ? "ring-1 ring-accent-soft" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {day.dayOfMonth}
                </span>
              )}
              {day.availability ? (
                <AvailabilityCount availability={day.availability} count={day.count ?? 0} onBlue={selected} />
              ) : day.dots ? (
                <Cancels dots={day.dots} onBlue={selected} />
              ) : (
                <span
                  aria-hidden="true"
                  className={[
                    "mt-0.5 text-[10px] font-semibold tabular-nums",
                    selected ? "text-inverse/80" : (day.count ?? 0) > 0 ? "text-accent" : "text-ink-3",
                  ].join(" ")}
                >
                  {(day.count ?? 0) > 0 ? day.count : "·"}
                </span>
              )}
            </>
          );
          const tile = [
            "flex w-full flex-col items-center gap-0.5 rounded-xl px-0.5 py-2",
            "transition-colors duration-150",
            selected ? "sk-tile [--sk-p:var(--color-accent)] text-inverse" : "text-ink",
          ];
          const spoken =
            day.label ??
            `${day.weekday} ${day.dayOfMonth}, ${day.count === 1 ? "1 session" : `${day.count ?? 0} sessions`}`;
          return (
            <li key={day.key} className="min-w-0 flex-1">
              {onSelect ? (
                <button
                  type="button"
                  onClick={() => onSelect(day.key)}
                  aria-pressed={selected}
                  aria-current={day.isToday ? "date" : undefined}
                  aria-label={spoken}
                  className={[...tile, selected ? "" : "active:bg-sunken"].join(" ")}
                >
                  {face}
                </button>
              ) : (
                <div aria-current={day.isToday ? "date" : undefined} className={tile.join(" ")}>
                  <span className="sr-only">{spoken}</span>
                  <span aria-hidden="true" className="contents">
                    {face}
                  </span>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {onBack ? (
        <div className="flex justify-center pt-1">
          <Button variant="ghost" onClick={onBack}>
            Back to today
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** The circle's radius and line width, in a 40 by 40 box. */
const RING_RADIUS = 17;
const RING_WIDTH = 2.5;
/** The space between two arcs, in degrees, so two participants read as two marks. */
const ARC_GAP = 16;

function point(degrees: number): string {
  const radians = ((degrees - 90) * Math.PI) / 180;
  return `${(20 + RING_RADIUS * Math.cos(radians)).toFixed(2)} ${(20 + RING_RADIUS * Math.sin(radians)).toFixed(2)}`;
}

/**
 * The date inside its circle: one circle for one participant, an arc each for more. Solid is
 * booked, dashed is a usual day not booked. On the blue tile every circle is white, so it reads; the
 * mark colours come back on the other days.
 */
function Ringed({ dots, onBlue, children }: { dots: WeekDayDot[]; onBlue: boolean; children: React.ReactNode }) {
  const marks = dots.filter((dot) => dot.kind !== "cancelled");
  const span = 360 / Math.max(1, marks.length);
  // The gap shrinks with the arcs, so a long family still gets one short arc each, never a negative one.
  const gap = Math.min(ARC_GAP, span / 3);
  return (
    // As wide as the day's column, 40 px at most, so circles never reach into the next day on a
    // narrow phone. The SVG scales with it.
    <span className="relative flex aspect-square w-full max-w-10 items-center justify-center text-[16px] leading-none font-semibold tabular-nums">
      {marks.length > 0 ? (
        <svg aria-hidden="true" viewBox="0 0 40 40" className="absolute inset-0 size-full" fill="none">
          {marks.map((dot, index) => {
            const stroke = onBlue ? "var(--color-inverse)" : markColor(dot.color);
            const dash = dot.kind === "open" ? "4 3" : undefined;
            if (marks.length === 1) {
              return (
                <circle
                  key={dot.participantId}
                  data-mark={dot.kind}
                  cx="20"
                  cy="20"
                  r={RING_RADIUS}
                  stroke={stroke}
                  strokeWidth={RING_WIDTH}
                  strokeDasharray={dash}
                />
              );
            }
            const from = index * span + gap / 2;
            const to = (index + 1) * span - gap / 2;
            return (
              <path
                key={dot.participantId}
                data-mark={dot.kind}
                d={`M ${point(from)} A ${RING_RADIUS} ${RING_RADIUS} 0 ${to - from > 180 ? 1 : 0} 1 ${point(to)}`}
                stroke={stroke}
                strokeWidth={RING_WIDTH}
                strokeDasharray={dash}
              />
            );
          })}
        </svg>
      ) : null}
      <span className="relative">{children}</span>
    </span>
  );
}

/** A red dash under the date per participant whose session the business cancelled. */
function Cancels({ dots, onBlue }: { dots: WeekDayDot[]; onBlue: boolean }) {
  const cancelled = dots.filter((dot) => dot.kind === "cancelled");
  return (
    <span aria-hidden="true" className="flex h-1.5 items-center justify-center gap-[3px]">
      {cancelled.map((dot) => (
        <span
          key={dot.participantId}
          data-mark="cancelled"
          className="h-0.5 w-2.5 rounded-full"
          style={{ backgroundColor: onBlue ? "var(--color-inverse)" : "var(--color-cancelled)" }}
        />
      ))}
    </span>
  );
}
